import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { apiKey } from "../../config/config";
import { languageHelper, refreshLanguage } from "../../config/helperFunctions";
import { toast } from "react-toastify";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import CustomerCardModal from "../../components/Modal/CustomerCardModal";
import {
  FaUser,
  FaEnvelope,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaIdCard,
  FaLock,
  FaArrowLeft,
  FaSignOutAlt,
  FaStore,
  FaUserPlus,
  FaEye,
  FaEyeSlash,
  FaRedoAlt,
} from "react-icons/fa";

export default function CustomerSignup() {
  const navigate = useNavigate();

  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("rms-admin"));
    } catch {
      return null;
    }
  });

  const [currentLang, setCurrentLang] = useState(
    () => localStorage.getItem("rms-lang") || "text_en"
  );
  const isRTL = currentLang === "text_ar";

  const [outletData, setOutletData] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  // Form State
  const initialFormState = {
    name: "",
    phone: "",
    email: "",
    customer_vat: "",
    date_of_birth: "",
    address: "",
    password: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showPasswordText, setShowPasswordText] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  // Success Modal State
  const [successCustomer, setSuccessCustomer] = useState(null);
  const [countdown, setCountdown] = useState(15);
  const countdownTimerRef = useRef(null);

  // 1. Authentication Guard: Accessible ONLY after user logs in
  useEffect(() => {
    const adminSession = JSON.parse(localStorage.getItem("rms-admin") || "null");
    if (!adminSession || !adminSession.token) {
      toast.warn("Please log in to access Customer Registration.", { theme: "colored" });
      navigate("/admin", { replace: true });
    } else {
      setUser(adminSession);
    }
  }, [navigate]);

  // 2. Fetch Outlet Info and Logo
  useEffect(() => {
    if (!user?.token) return;

    if (user?.user?.outlet_id) {
      axios
        .get(`${apiKey}/api/rms/admin/get-outlets/${user.user.outlet_id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        })
        .then((res) => {
          setOutletData(res.data && res.data[0]);
        })
        .catch(() => {});
    }

    axios
      .get(`${apiKey}/api/rms/customer/settings`)
      .then((res) => {
        const logoItem = res.data?.find((item) => item.type === "logo");
        if (logoItem?.value) {
          try {
            const parsed = JSON.parse(logoItem.value);
            if (parsed?.logo_image_url) {
              setLogoUrl(parsed.logo_image_url);
            }
          } catch {}
        }
      })
      .catch(() => {});
  }, [user?.token, user?.user?.outlet_id]);

  // 3. Language Switcher
  const toggleLanguage = () => {
    const nextLang = currentLang === "text_en" ? "text_ar" : "text_en";
    localStorage.setItem("rms-lang", nextLang);
    setCurrentLang(nextLang);
    if (typeof refreshLanguage === "function") {
      refreshLanguage();
    }
  };

  // 4. Input Handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formError) setFormError("");
  };

  // 5. Reset Form
  const handleResetForm = () => {
    setFormData(initialFormState);
    setShowPasswordSection(false);
    setFormError("");
  };

  // 6. Submit Registration
  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.name.trim()) {
      setFormError(languageHelper("Customer Name is required."));
      return;
    }

    if (!formData.phone || formData.phone.length < 7) {
      setFormError(languageHelper("Please enter a valid phone number."));
      return;
    }

    setLoading(true);

    const payload = {
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim() || null,
      customer_vat: formData.customer_vat.trim() || null,
      date_of_birth: formData.date_of_birth || null,
      address: formData.address.trim() || null,
      password: formData.password || "12345678",
    };

    axios
      .post(`${apiKey}/api/rms/admin/customer`, payload, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        setLoading(false);
        const registered = res.data?.data || {
          id: Math.floor(10000 + Math.random() * 90000),
          name: payload.name,
          phone: payload.phone,
          email: payload.email,
        };

        toast.success(
          res.data?.message || languageHelper("Customer registered successfully!"),
          { theme: "colored" }
        );

        setSuccessCustomer(registered);
        handleResetForm();
        setCountdown(15);
      })
      .catch((err) => {
        setLoading(false);
        const resData = err.response?.data;
        if (resData?.errors) {
          const firstKey = Object.keys(resData.errors)[0];
          const errorMsg = resData.errors[firstKey]?.[0] || "Validation Error";
          setFormError(errorMsg);
          toast.error(errorMsg, { theme: "colored" });
        } else {
          const msg = resData?.message || "Registration failed. Please try again.";
          setFormError(msg);
          toast.error(msg, { theme: "colored" });
        }
      });
  };

  // 7. Auto-Reset Countdown for Success Modal
  useEffect(() => {
    if (!successCustomer) return;

    countdownTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimerRef.current);
          setSuccessCustomer(null);
          return 15;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
  }, [successCustomer]);

  const handleDismissSuccess = () => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    setSuccessCustomer(null);
    setCountdown(15);
  };

  // 8. Logout
  const handleLogout = () => {
    axios
      .post(
        `${apiKey}/api/rms/admin/logout`,
        {},
        {
          headers: { Authorization: `Bearer ${user?.token}` },
        }
      )
      .finally(() => {
        localStorage.removeItem("rms-admin");
        navigate("/admin");
      });
  };

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="min-h-screen bg-gray-100 flex flex-col font-sans"
    >
      {/* ================= TOP NAVIGATION BAR ================= */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {/* Brand & Title */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-d-bg text-white flex items-center justify-center shadow-sm">
              <FaUserPlus className="text-xl" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold text-d-bg-text leading-tight">
                  {outletData?.name || "Imdady RMS"}
                </h1>
                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-semibold border border-gray-200">
                  {languageHelper("Customer Registration")}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                {languageHelper("Instant customer enrollment")}
              </p>
            </div>
          </div>

          {/* Active Status Badge & Navigation Controls */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Active Outlet / Cashier Info */}
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded bg-gray-50 border border-gray-200 text-xs text-gray-600">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
              <FaStore className="text-gray-400" />
              <span className="font-medium text-gray-700">
                {outletData?.name || languageHelper("Active Outlet")}
              </span>
              <span className="text-gray-300">|</span>
              <span className="text-gray-500">
                {user?.user?.name || languageHelper("Cashier")}
              </span>
            </div>

            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition shadow-sm"
              title="Change Language"
            >
              <span>🌐</span>
              <span>{currentLang === "text_en" ? "العربية" : "English"}</span>
            </button>

            {/* Return to Dashboard */}
            <Link
              to="/admin/home"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded bg-d-cat-btn text-white hover:opacity-90 transition shadow-sm"
              title={languageHelper("Return to Dashboard")}
            >
              <FaArrowLeft className={isRTL ? "rotate-180" : ""} />
              <span className="hidden sm:inline">{languageHelper("Dashboard")}</span>
            </Link>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition"
              title={languageHelper("Logout")}
            >
              <FaSignOutAlt className="text-base" />
            </button>
          </div>
        </div>
      </header>

      {/* ================= MAIN CONTENT ================= */}
      <main className="flex-1 max-w-4xl w-full mx-auto p-4 sm:p-6 lg:p-8 flex flex-col justify-center">
        {/* Simple Form Card */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6 sm:p-8">
          {/* Card Header */}
          <div className="flex justify-between items-center pb-4 mb-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-d-bg-text">
                {languageHelper("Customer Registration")}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {languageHelper("Enter customer details to register.")}
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
              <span className="font-bold text-d-bg-text">0</span>
              <span>{languageHelper("Starting Loyalty Points")}</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error Message Alert */}
            {formError && (
              <div className="p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
                <span className="text-red-500 font-bold mt-0.5">⚠️</span>
                <div className="flex-1">
                  <p className="font-semibold">{languageHelper("Registration Error")}</p>
                  <p>{formError}</p>
                </div>
              </div>
            )}

            {/* Customer Basic Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Customer Name */}
              <div>
                <label className="block text-xs font-bold text-d-bg-text mb-1">
                  {languageHelper("Customer Name")}{" "}
                  <span className="text-red-500 font-bold">*</span>
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 start-0 flex items-center ps-3 text-gray-400 pointer-events-none">
                    <FaUser />
                  </span>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={languageHelper("Customer Name")}
                    className="w-full ps-10 pe-3 py-2 bg-white border border-gray-300 rounded text-sm focus:border-d-bg focus:ring-1 focus:ring-d-bg outline-none"
                  />
                </div>
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-xs font-bold text-d-bg-text mb-1">
                  {languageHelper("Phone")}{" "}
                  <span className="text-red-500 font-bold">*</span>
                  <span className="text-gray-400 font-normal ms-1 text-[11px]">
                    ({languageHelper("with country code")})
                  </span>
                </label>
                <div className="relative">
                  <PhoneInput
                    defaultCountry="sa"
                    name="phone"
                    value={formData.phone}
                    onChange={(phone) => {
                      setFormData((prev) => ({ ...prev, phone }));
                      if (formError) setFormError("");
                    }}
                    className="w-full"
                    inputClassName="!w-full !py-2 !h-[38px] !bg-white !border !border-gray-300 !rounded-r !text-sm focus:!border-d-bg !outline-none"
                    countrySelectorStyleProps={{
                      buttonClassName:
                        "!h-[38px] !bg-gray-100 !border !border-gray-300 !border-r-0 !rounded-l !px-2.5",
                    }}
                  />
                </div>
              </div>

              {/* Email Address */}
              <div>
                <label className="block text-xs font-bold text-d-bg-text mb-1">
                  {languageHelper("Email")}
                  <span className="text-gray-400 font-normal ms-1 text-[11px]">
                    ({languageHelper("Optional")})
                  </span>
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 start-0 flex items-center ps-3 text-gray-400 pointer-events-none">
                    <FaEnvelope />
                  </span>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="customer@example.com"
                    className="w-full ps-10 pe-3 py-2 bg-white border border-gray-300 rounded text-sm focus:border-d-bg focus:ring-1 focus:ring-d-bg outline-none"
                  />
                </div>
              </div>

              {/* Date of Birth */}
              <div>
                <label className="block text-xs font-bold text-d-bg-text mb-1">
                  {languageHelper("Date Of Birth")}
                  <span className="text-gray-400 font-normal ms-1 text-[11px]">
                    ({languageHelper("Optional")})
                  </span>
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 start-0 flex items-center ps-3 text-gray-400 pointer-events-none">
                    <FaCalendarAlt />
                  </span>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    className="w-full ps-10 pe-3 py-2 bg-white border border-gray-300 rounded text-sm focus:border-d-bg focus:ring-1 focus:ring-d-bg outline-none"
                  />
                </div>
              </div>

              {/* Customer VAT Number */}
              <div>
                <label className="block text-xs font-bold text-d-bg-text mb-1">
                  {languageHelper("Customer Vat No")}
                  <span className="text-gray-400 font-normal ms-1 text-[11px]">
                    ({languageHelper("Optional")})
                  </span>
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 start-0 flex items-center ps-3 text-gray-400 pointer-events-none">
                    <FaIdCard />
                  </span>
                  <input
                    type="text"
                    name="customer_vat"
                    value={formData.customer_vat}
                    onChange={handleChange}
                    placeholder={languageHelper("Customer Vat No")}
                    className="w-full ps-10 pe-3 py-2 bg-white border border-gray-300 rounded text-sm focus:border-d-bg focus:ring-1 focus:ring-d-bg outline-none"
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="block text-xs font-bold text-d-bg-text mb-1">
                  {languageHelper("Address")}
                  <span className="text-gray-400 font-normal ms-1 text-[11px]">
                    ({languageHelper("Optional")})
                  </span>
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 start-0 flex items-center ps-3 text-gray-400 pointer-events-none">
                    <FaMapMarkerAlt />
                  </span>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder={languageHelper("Address")}
                    className="w-full ps-10 pe-3 py-2 bg-white border border-gray-300 rounded text-sm focus:border-d-bg focus:ring-1 focus:ring-d-bg outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Optional Custom Password */}
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setShowPasswordSection(!showPasswordSection)}
                className="text-xs font-semibold text-d-bg-text hover:underline flex items-center gap-1"
              >
                <FaLock className="text-[10px]" />
                {showPasswordSection
                  ? languageHelper("Hide App Password Setting")
                  : languageHelper("Set Custom App Password (Optional)")}
              </button>

              {showPasswordSection && (
                <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-200">
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    {languageHelper("Password for Customer App / Web")}
                  </label>
                  <p className="text-[11px] text-gray-500 mb-2">
                    {languageHelper(
                      "Leave blank to automatically assign default password (12345678)."
                    )}
                  </p>
                  <div className="relative max-w-sm">
                    <span className="absolute inset-y-0 start-0 flex items-center ps-3 text-gray-400 pointer-events-none">
                      <FaLock />
                    </span>
                    <input
                      type={showPasswordText ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Minimum 6 characters"
                      className="w-full ps-10 pe-10 py-1.5 bg-white border border-gray-300 rounded text-sm focus:border-d-bg focus:ring-1 focus:ring-d-bg outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordText(!showPasswordText)}
                      className="absolute inset-y-0 end-0 flex items-center pe-3 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswordText ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Dynamic Divider using tailwind bg-d-bg */}
            <span className="w-full bg-d-bg h-0.5 my-3 inline-block"></span>

            {/* Form Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={loading}
                className="bg-d-cat-btn text-white text-sm px-5 py-2 rounded shadow hover:opacity-90 outline-none focus:outline-none transition-all flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      ></path>
                    </svg>
                    {languageHelper("Submitting...")}
                  </>
                ) : (
                  <>
                    <FaUserPlus />
                    {languageHelper("Submit")}
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleResetForm}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm px-4 py-2 rounded shadow transition flex items-center gap-1.5"
              >
                <FaRedoAlt className="text-xs" />
                {languageHelper("Clear")}
              </button>
            </div>
          </form>
        </div>
      </main>

      {/* ================= DIGITAL MEMBERSHIP CARD POPUP ================= */}
      {successCustomer && (
        <CustomerCardModal
          customer={successCustomer}
          onClose={handleDismissSuccess}
          onRegisterNext={handleDismissSuccess}
          countdown={countdown}
          outletData={outletData}
          logoUrl={logoUrl}
        />
      )}
    </div>
  );
}
